/**
 * API Utility Functions
 * 
 * This module provides utility functions for making API calls to the backend.
 * Includes error handling and response formatting.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002';

/**
 * Make an API request with error handling
 * @param {string} endpoint - The API endpoint (without base URL)
 * @param {object} options - Fetch options (method, body, headers, etc.)
 * @returns {Promise<object>} - The response data
 */
export const apiRequest = async (endpoint, options = {}) => {
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    };

    const response = await fetch(url, defaultOptions);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
    
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

/**
 * Task API functions
 */
export const taskApi = {
  // Get all task statuses
  getStatuses: () => apiRequest('/api/tasks/statuses'),
  
  // Get all clients
  getClients: () => apiRequest('/api/tasks/clients'),
  
  // Get tasks with filtering and pagination
  getTasks: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/api/tasks?${queryString}`);
  },
  
  // Get a specific task
  getTask: (taskId) => apiRequest(`/api/tasks/${taskId}`),
  
  // Create a new task
  createTask: (taskData) => apiRequest('/api/tasks', {
    method: 'POST',
    body: JSON.stringify(taskData)
  }),
  
  // Update a task
  updateTask: (taskId, taskData) => apiRequest(`/api/tasks/${taskId}`, {
    method: 'PUT',
    body: JSON.stringify(taskData)
  }),
  
  // Delete a task
  deleteTask: (taskId) => apiRequest(`/api/tasks/${taskId}`, {
    method: 'DELETE'
  }),
  
  // Bulk archive tasks
  bulkArchiveTasks: (taskIds) => apiRequest('/api/tasks/bulk/archive', {
    method: 'POST',
    body: JSON.stringify({ task_ids: taskIds })
  }),
  
  // Bulk unarchive tasks
  bulkUnarchiveTasks: (taskIds) => apiRequest('/api/tasks/bulk/unarchive', {
    method: 'POST',
    body: JSON.stringify({ task_ids: taskIds })
  }),
  
  // Bulk delete tasks (requires superadmin)
  bulkDeleteTasks: (taskIds, authToken) => apiRequest('/api/tasks/bulk/delete', {
    method: 'POST',
    body: JSON.stringify({ task_ids: taskIds }),
    headers: {
      'Content-Type': 'application/json',
      'Authorization': authToken
    }
  }),
  
  // Get task files
  getTaskFiles: (taskId) => apiRequest(`/api/tasks/${taskId}/files`),
  
  // Download task file
  downloadTaskFile: (taskId, fileId) => apiRequest(`/api/tasks/${taskId}/files/${fileId}/download`),
  
  // Get task statistics
  getStatistics: () => apiRequest('/api/tasks/statistics')
};

/**
 * General utility functions
 */
export const utils = {
  /**
   * Format file size in human readable format
   * @param {number} bytes - File size in bytes
   * @returns {string} - Formatted file size
   */
  formatFileSize: (bytes) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },
  
  /**
   * Get priority color for display
   * @param {string} priority - Task priority
   * @returns {string} - Color code
   */
  getPriorityColor: (priority) => {
    const colors = {
      critical: '#ff4d4f',
      high: '#fa8c16',
      medium: '#1890ff',
      low: '#52c41a'
    };
    return colors[priority] || '#d9d9d9';
  },
  
  /**
   * Get priority badge properties
   * @param {string} priority - Task priority
   * @returns {object} - Badge properties
   */
  getPriorityBadge: (priority) => {
    const badges = {
      critical: { color: 'red', text: 'Critical' },
      high: { color: 'orange', text: 'High' },
      medium: { color: 'blue', text: 'Medium' },
      low: { color: 'green', text: 'Low' }
    };
    return badges[priority] || { color: 'default', text: priority };
  }
};

const api = {
  apiRequest,
  taskApi,
  utils,
  // Basic HTTP methods for convenience
  get: (endpoint, options = {}) => apiRequest(endpoint, { method: 'GET', ...options }),
  post: (endpoint, data, options = {}) => apiRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
    ...options
  }),
  put: (endpoint, data, options = {}) => apiRequest(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
    ...options
  }),
  delete: (endpoint, options = {}) => apiRequest(endpoint, { method: 'DELETE', ...options })
};

export default api;
